import { motion } from "motion/react"

interface ActionButtonsProps {
  canCheck: boolean
  callAmount: number
  isMobile?: boolean
  onFold: () => void
  onCheck: () => void
  onCall: () => void
}

const fontStyle = { fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }

const ActionButtons = ({ canCheck, callAmount, isMobile, onFold, onCheck, onCall }: ActionButtonsProps) => {
  return (
    <div className={isMobile ? "grid grid-cols-3 gap-2 w-full" : "flex gap-2"}>
      {/* Fold — red, always enabled */}
      <motion.button
        onClick={onFold}
        className="relative flex flex-col items-center justify-center rounded-xl font-bold border-2 border-red-400/30 select-none bg-red-500/90 text-white shadow-[0_0_12px_rgba(239,68,68,0.2)] hover:bg-red-500 hover:shadow-[0_0_18px_rgba(239,68,68,0.35)] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-base"
        style={fontStyle}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.93, backgroundColor: "#b91c1c" }}
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
      >
        <span className="text-xs font-medium opacity-60 leading-none mb-0.5 md:hidden">×</span>
        Fold
      </motion.button>

      {/* Check — gray/neutral, enabled only when no bet to call */}
      <motion.button
        onClick={onCheck}
        disabled={!canCheck}
        className={"relative flex flex-col items-center justify-center rounded-xl font-bold border-2 select-none px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-base transition-colors " + (
          canCheck
            ? "bg-white/10 border-white/20 text-white shadow-[0_0_8px_rgba(255,255,255,0.05)] hover:bg-white/15 hover:border-white/30"
            : "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
        )}
        style={fontStyle}
        whileHover={canCheck ? { scale: 1.04 } : undefined}
        whileTap={canCheck ? { scale: 0.93, backgroundColor: "rgba(255,255,255,0.2)" } : undefined}
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
      >
        <span className={"text-xs font-medium leading-none mb-0.5 md:hidden " + (canCheck ? "opacity-60" : "opacity-20")}>✓</span>
        Check
      </motion.button>

      {/* Call — green, enabled only when there's a bet to call */}
      <motion.button
        onClick={onCall}
        disabled={canCheck || callAmount <= 0}
        className={"relative flex flex-col items-center justify-center rounded-xl font-bold border-2 select-none px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-base transition-colors " + (
          !canCheck && callAmount > 0
            ? "bg-emerald-500/90 border-emerald-400/30 text-white shadow-[0_0_12px_rgba(16,185,129,0.2)] hover:bg-emerald-500 hover:shadow-[0_0_18px_rgba(16,185,129,0.35)]"
            : "bg-emerald-500/20 border-emerald-400/10 text-white/30 cursor-not-allowed"
        )}
        style={fontStyle}
        whileHover={!canCheck && callAmount > 0 ? { scale: 1.04 } : undefined}
        whileTap={!canCheck && callAmount > 0 ? { scale: 0.93, backgroundColor: "#047857" } : undefined}
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
      >
        <span className={"text-xs font-medium leading-none mb-0.5 md:hidden " + (!canCheck && callAmount > 0 ? "opacity-60" : "opacity-20")}>{callAmount > 0 ? callAmount.toLocaleString() : "—"}</span>
        {!canCheck && callAmount > 0 ? "Call " + callAmount.toLocaleString() : "Call"}
      </motion.button>
    </div>
  )
}

export default ActionButtons
